/**
 * useSelectedSlide
 *
 * Fine-grained Zustand selector that returns the currently selected slide
 * together with its mutation handler methods.  Components that only need the
 * slide data will re-render only when that specific slide changes — not when
 * any other part of presentation state changes.
 *
 * This replaces the `selectedSlide` field that was previously spread from
 * `useRoSContext()`.
 */

import { useMemo } from 'react';
import type React from 'react';
import type {
  Interaction,
  InteractionColors,
  InteractionOptions,
  InteractionSettings,
} from '@components/Presentation/types';
import { usePresentationStore } from '../store/presentationStore';

export type TSlideHandlers = {
  applyToSimilarSlides?: () => void;
  onTitleChange?: (value: string) => void;
  onOptionChange?: (options: InteractionOptions[]) => void;
  updateTitle: (e: React.ChangeEvent<HTMLInputElement>) => void;
  updateSetting: (key: keyof InteractionSettings, value: any) => void;
  updateQuizOptions: (index: number, checked: boolean) => void;
  updateColor: (key: keyof InteractionColors, value: string | boolean) => void;
  addOption: () => void;
  addBulkOptions: (count: number) => void;
  updateOptions: (
    index?: number,
    e?: React.ChangeEvent<HTMLInputElement>,
    choiceCircleOptions?: InteractionOptions[]
  ) => void;
  updateOptionsByName: (
    index?: number,
    e?: React.ChangeEvent<HTMLInputElement>
  ) => void;
  deleteOption: (index: number) => void;
  resetDesign: () => void;
};

export type SelectedSlide = Interaction & { handler: TSlideHandlers };

/**
 * Returns the currently selected slide object (with all its data) plus the
 * stable `handler` bag of mutation callbacks.
 *
 * Matches the old `selectedSlide` shape from `useRoSContext()` exactly.
 */
export const useSelectedSlide = (): SelectedSlide => {
  // Reactive — re-renders when the selected slide content changes
  const slide = usePresentationStore((s) => s.slides[s.selectedSlideIndex]);

  // Actions are stable references in Zustand (defined once in `create()`),
  // so the handler object never needs to be recreated.
  const handler = useMemo<TSlideHandlers>(() => {
    const s = usePresentationStore.getState();
    return {
      applyToSimilarSlides: s.applyToSimilarSlides,
      onTitleChange: (value: string) =>
        s.updateTitle({ target: { value } } as React.ChangeEvent<HTMLInputElement>),
      onOptionChange: s.onOptionChange,
      updateTitle: s.updateTitle,
      updateSetting: s.updateSetting,
      updateQuizOptions: s.updateQuizOptions,
      updateColor: s.updateColor,
      addOption: s.addOption,
      addBulkOptions: s.addBulkOptions,
      updateOptions: s.updateOptions,
      updateOptionsByName: s.updateOptionsByName,
      deleteOption: s.deleteOption,
      resetDesign: s.resetDesign,
    };
  }, []); // empty deps — Zustand actions are stable

  // Mirror original context behaviour: spread undefined → {} so callers always
  // get an object even before slides have loaded.
  return useMemo(
    () => ({ ...(slide as Interaction | undefined), handler }) as SelectedSlide,
    [slide, handler]
  );
};
